<script setup>
import { computed } from 'vue'
import { useRouter } from 'vue-router'
import copyToClipBoard from '@utils/copy-to-clipboard'
import { siteUrl } from '@/config/site'
import { shareOnFacebook, shareOnX } from '@utils/social-share'
import { useAppearanceStore } from '@stores/appearance'
import { useMessages } from '@composables/use-messages'
import { useFavouriteToggle } from '@composables/use-favourite-toggle'

const props = defineProps({
    artist: {
        type: Object,
        default: () => {},
    },
    hasViewArtistOption: {
        type: Boolean,
        default: true,
    },
})

const appearanceStore = useAppearanceStore()
const router = useRouter()
const getMessageText = useMessages()
const favourites = useFavouriteToggle()

const url = computed(() => {
    // Router 4 throws on a falsy required param, and this resolves during render.
    // `artistUrl` is optional in the route; `id` is not.
    if (!props.artist.id) return ''

    const href = router.resolve({
        name: 'artist',
        params: { artistUrl: props.artist.url, id: props.artist.id },
    }).href
    return siteUrl(href)
})

const isFollowing = computed(() => Boolean(props.artist.id) && favourites.isFavourite({ type: 'artists', id: props.artist.id }))

const header = computed(() => ({
    image: props.artist,
    imageType: 'photo',
    title: props.artist.title,
    subtitle: 'Artist',
}))

function copyLinkToClipBoard() {
    copyToClipBoard(url.value)
    appearanceStore.handleNotification({
        message: getMessageText('LINK_COPIED_CLIPBOARD'),
        type: 'success',
    })
}

// A row whose entity has no id is hidden: the View row's router-link would throw resolving
// a missing param, and master's follow button went inert (pointer-events: none) for it.
const items = computed(() => [
    {
        id: 'view',
        label: 'View Artist',
        to: { name: 'artist', params: { artistUrl: props.artist.url, id: props.artist.id } },
        hidden: !props.hasViewArtistOption || !props.artist.id,
    },
    { divider: true },
    {
        id: 'follow-artist',
        label: isFollowing.value ? 'Following' : 'Follow Artist',
        hidden: !props.artist.id,
        action: () => favourites.toggle({ type: 'artists', id: props.artist.id, addedKey: 'FAV_ARTIST_ADDED', removedKey: 'FAV_ARTIST_REMOVED' }),
    },
    { divider: true },
    // Interim (R8): today's three share actions as plain rows. The single `Share ›` row
    // lands with the Share surface ticket 1218209213218817.
    { id: 'share-facebook', label: 'Facebook', action: () => shareOnFacebook(url.value) },
    { id: 'share-x', label: 'X', action: () => shareOnX(url.value, `${props.artist.title} on @friskyradio #feelinfrisky`) },
    { id: 'copy-link', label: 'Copy Artist Link', action: copyLinkToClipBoard },
])
</script>

<template>
    <BaseContextMenu
        class="artist-options"
        :items="items"
        :header="header"
        trigger-label="Artist options"
    >
        <template
            v-if="$slots.trigger"
            #trigger="slotProps"
        >
            <slot
                name="trigger"
                v-bind="slotProps"
            />
        </template>
    </BaseContextMenu>
</template>
